import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, TextField, InputAdornment, IconButton, Pagination, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import {
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import VideoPreviewCarousel from "./VideoPreviewCarousel";
import styled from "@emotion/styled";
import axios from "axios";
import { LoaderIcon } from "lucide-react";
import { API } from "../../config/env";

// Styled components
const VideoContainer = styled.div`
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: 20px;
    width: 100%;
    max-width: 100%;
`;

const VideoHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const VideoTitle = styled.h2`
    font-family: "Nunito Sans", sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin: 0;
`;

const SearchFilterContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const UniversitySelect = styled(FormControl)`
    min-width: 150px;
`;

const EmbedButton = styled(Button)`
    background-color: #4880ff;
    color: white;
    text-transform: none;
    font-family: "Nunito Sans", sans-serif;
    font-weight: 600;
    padding: 6px 16px;
    border-radius: 4px;
    font-size: 14px;
    &:hover {
        background-color: #3a6ad4;
    }
`;

// Filter dropdown styles
const FilterDropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const FilterDropdownButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 120px;
    text-transform: none;
    font-weight: 600;
    color: #4880ff;
    border-color: #4880ff;
    padding: 6px 12px;

    &:hover {
        background-color: rgba(72, 128, 255, 0.04);
    }
`;

const FilterDropdownContent = styled.div`
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: absolute;
    right: 0;
    top: 100%;
    background-color: white;
    min-width: 160px;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
    z-index: 10;
    border-radius: 4px;
    margin-top: 4px;
`;

const FilterOption = styled.div`
    padding: 10px 16px;
    cursor: pointer;
    font-family: "Nunito Sans", sans-serif;
    font-size: 14px;

    &:hover {
        background-color: #f5f5f5;
    }

    &.active {
        background-color: rgba(72, 128, 255, 0.1);
        font-weight: 600;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding: 20px 0;
`;

// Mock data for demonstration
const mockData = [
    {
        id: 1,
        name: "Yuzana",
        program: "LLM in American Law",
        country: "UK",
        views: "Loading..",
        video_id: "oejxdipcf4",
        url: "https://portal.collegeit.org/watch?id=oejxdipcf4",
        status: "Delivered",
        comments: [],
        embedCode: `<a href="https://portal.collegeit.org/watch?id=oejxdipcf4"><img src="https://embed-ssl.wistia.com/deliveries/f52521f06a60e4d2bf389ae2fa31333f.jpg" alt="video1" width="450" height="253" /></a>`,
    },
    {
        id: 2,
        name: "Dylan",
        program: "LLM in American Law",
        country: "USA",
        views: 3,
        video_id: "2p12wy7rt0",
        url: "https://portal.collegeit.org/watch?id=2p12wy7rt0",
        status: "Draft",
        comments: [],
        embedCode: `<a href="https://portal.collegeit.org/watch?id=2p12wy7rt0"><img src="https://embed-ssl.wistia.com/deliveries/6d754130dcb6ae8721bd657c03a9e6eb.jpg" alt="video2" width="450" height="253" /></a>`,
    },
    {
        id: 3,
        name: "Zicheng",
        program: "JD",
        country: "Thailand",
        views: 7,
        video_id: "p440xkjx75",
        url: "https://portal.collegeit.org/watch?id=p440xkjx75",
        status: "Delivered",
        comments: [],
        embedCode: `<a href="https://portal.collegeit.org/watch?id=p440xkjx75"><img src="https://embed-ssl.wistia.com/deliveries/cd0b311c75519a8857fc27a1f6ed2ddc.jpg" alt="video3" width="450" height="253" /></a>`,
    },
];
// const mockData = [
//     {
//         id: 1,
//         name: "Yuzana",
//         program: "LLM in American Law",
//         country: "UK",
//         views: "Loading..",
//         video_id: "oejxdipcf4",
//         url: "https://portal.collegeit.org/watch?id=oejxdipcf4",
//         status: "Delivered",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=oejxdipcf4"><img src="https://embed-ssl.wistia.com/deliveries/f52521f06a60e4d2bf389ae2fa31333f.jpg" alt="video1" width="450" height="253" /></a>`
//     },
//     {
//         id: 2,
//         name: "Dylan",
//         program: "LLM in American Law",
//         country: "USA",
//         views: 3,
//         video_id: "2p12wy7rt0",
//         url: "https://portal.collegeit.org/watch?id=2p12wy7rt0",
//         status: "Draft",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=2p12wy7rt0"><img src="https://embed-ssl.wistia.com/deliveries/6d754130dcb6ae8721bd657c03a9e6eb.jpg" alt="video2" width="450" height="253" /></a>`
//     },
//     {
//         id: 3,
//         name: "Zicheng",
//         program: "JD",
//         country: "Thailand",
//         views: 7,
//         video_id: "p440xkjx75",
//         url: "https://portal.collegeit.org/watch?id=p440xkjx75",
//         status: "Delivered",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=p440xkjx75"><img src="https://embed-ssl.wistia.com/deliveries/cd0b311c75519a8857fc27a1f6ed2ddc.jpg" alt="video3" width="450" height="253" /></a>`
//     },
//     {
//         id: 4,
//         name: "Sarah",
//         program: "JD",
//         country: "Canada",
//         views: 12,
//         video_id: "abc123xyz",
//         url: "https://portal.collegeit.org/watch?id=abc123xyz",
//         status: "Delivered",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=abc123xyz"><img src="https://embed-ssl.wistia.com/deliveries/sample1.jpg" alt="video4" width="450" height="253" /></a>`
//     },
//     {
//         id: 5,
//         name: "Michael",
//         program: "LLM in Tax Law",
//         country: "Australia",
//         views: 5,
//         video_id: "def456uvw",
//         url: "https://portal.collegeit.org/watch?id=def456uvw",
//         status: "Draft",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=def456uvw"><img src="https://embed-ssl.wistia.com/deliveries/sample2.jpg" alt="video5" width="450" height="253" /></a>`
//     },
//     {
//         id: 6,
//         name: "Emma",
//         program: "JD",
//         country: "UK",
//         views: 15,
//         video_id: "ghi789rst",
//         url: "https://portal.collegeit.org/watch?id=ghi789rst",
//         status: "Delivered",
//         comments: [],
//         embedCode: `<a href="https://portal.collegeit.org/watch?id=ghi789rst"><img src="https://embed-ssl.wistia.com/deliveries/sample3.jpg" alt="video6" width="450" height="253" /></a>`
//     },
//     ...Array.from({ length: 24 }, (_, i) => {
//         const id = i + 7;
//         return {
//             id,
//             name: `Student${id}`,
//             program: id % 3 === 0 ? "JD" : id % 3 === 1 ? "LLM in American Law" : "LLM in Tax Law",
//             country: ["UK", "USA", "Thailand", "Canada", "Australia"][i % 5],
//             views: Math.floor(Math.random() * 20),
//             video_id: `video${id}abc`,
//             url: `https://portal.collegeit.org/watch?id=video${id}abc`,
//             status: id % 2 === 0 ? "Draft" : "Delivered",
//             comments: [],
//             embedCode: `<a href="https://portal.collegeit.org/watch?id=video${id}abc"><img src="https://embed-ssl.wistia.com/deliveries/sample${id}.jpg" alt="video${id}" width="450" height="253" /></a>`
//         };
//     })
// ];


// Cache management functions
const getCachedMetrics = (video_id) => {
    const cached = sessionStorage.getItem(`video_metrics_${video_id}`);
    if (cached) {
        try {
            const { data, timestamp } = JSON.parse(cached);
            // Cache expires after 15 minutes
            if (Date.now() - timestamp < 15 * 60 * 1000) {
                return data;
            }
            sessionStorage.removeItem(`video_metrics_${video_id}`);
        } catch (error) {
            console.error('Error parsing cached metrics:', error);
            sessionStorage.removeItem(`video_metrics_${video_id}`);
        }
    }
    return null;
};

const setCachedMetrics = (video_id, metricsData) => {
    try {
        sessionStorage.setItem(
            `video_metrics_${video_id}`,
            JSON.stringify({
                data: metricsData,
                timestamp: Date.now(),
            })
        );
    } catch (error) {
        console.error('Error caching metrics:', error);
    }
};

const VideoEngagementTable = ({ isAdmin = false, universityId }) => {
    const [allData, setAllData] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewsLoading, setViewsLoading] = useState({});
    const [metrics, setMetrics] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [filterOption, setFilterOption] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageData, setPageData] = useState([]);
    const [selectedUniversity, setSelectedUniversity] = useState(isAdmin ? "all" : universityId);
    const itemsPerPage = 25;
    const filterButtonRef = useRef(null);
    const [pollingInterval, setPollingInterval] = useState(null);

    // Fetch data for selected university
    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                if (selectedUniversity === "all" && isAdmin) {
                    const universities = ["boston", "usc", "texas"];
                    const fetchPromises = universities.map(university =>
                        axios.get(
                            `${API.CORE}/api/v1/video-analytics/all?university=${university}`
                        )
                    );

                    const responses = await Promise.all(fetchPromises);
                    const allFetchedData = responses.reduce((acc, response) => {
                        const universityData = response.data.data || [];
                        return [...acc, ...universityData];
                    }, []);

                    setAllData(allFetchedData);
                    setData(allFetchedData);
                } else {
                    const response = await axios.get(
                        `${API.CORE}/api/v1/video-analytics/all?university=${selectedUniversity}`
                    );
                    const fetchedData = response.data.data || [];
                    setAllData(fetchedData);
                    setData(fetchedData);
                }
            } catch (error) {
                console.error("Error fetching video data:", error);
                setAllData(mockData);
                setData(mockData);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAllData();
    }, [selectedUniversity, isAdmin]);

    const handleOpenPreview = (index) => {
        setSelectedVideoIndex(index);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    const handleFilterOptionClick = (option) => {
        setFilterOption(option);
        setFilterDropdownOpen(false);
    };

    // Filter data based on search term and selected filter option
    const filteredData = React.useMemo(() => {
        if (!data) return [];
        return data.filter((item) => {
            const matchesSearch =
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.program?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.country?.toLowerCase().includes(searchTerm.toLowerCase());

            let matchesFilter = true;
            if (filterOption === "most_views") {
                matchesFilter = true;
            } else if (filterOption === "no_views") {
                const metrics = getCachedMetrics(item.video_id);
                matchesFilter = metrics ? metrics.play_count === 0 : false;
            }

            return matchesSearch && matchesFilter;
        });
    }, [data, searchTerm, filterOption]);

    // Get current page items
    const getCurrentPageItems = useCallback(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        
        let sortedData = [...filteredData];
        if (filterOption === "most_views") {
            sortedData.sort((a, b) => {
                const metricsA = getCachedMetrics(a.video_id);
                const metricsB = getCachedMetrics(b.video_id);
                const viewsA = metricsA ? metricsA.play_count : 0;
                const viewsB = metricsB ? metricsB.play_count : 0;
                return viewsB - viewsA;
            });
        }
        
        return sortedData.slice(startIndex, endIndex);
    }, [currentPage, filteredData, filterOption]);

    // Update page data when filtered data changes
    useEffect(() => {
        setPageData(getCurrentPageItems());
    }, [getCurrentPageItems, filteredData]);

    // Initialize metrics state with cached data
    useEffect(() => {
        if (pageData.length > 0) {
            const initialMetrics = {};
            const initialLoadingState = {};
            
            pageData.forEach(video => {
                const cachedData = getCachedMetrics(video.video_id);
                if (cachedData) {
                    initialMetrics[video.video_id] = cachedData;
                    initialLoadingState[video.video_id] = false;
                } else {
                    initialLoadingState[video.video_id] = true;
                }
            });
            
            setMetrics(initialMetrics);
            setViewsLoading(initialLoadingState);
        }
    }, [pageData]);

    // Fetch video metrics for the current page
    const fetchVideoMetrics = useCallback(async () => {
        const currentItems = getCurrentPageItems();
        const newLoadingStates = { ...viewsLoading };
        const fetchPromises = [];

        currentItems.forEach(video => {
            // Skip if video_id is missing
            if (!video.video_id) {
                console.error('Missing video_id for video:', video);
                return;
            }

            const cachedMetrics = getCachedMetrics(video.video_id);
            if (cachedMetrics) {
                setMetrics(prev => ({
                    ...prev,
                    [video.video_id]: cachedMetrics
                }));
                newLoadingStates[video.video_id] = false;
            } else {
                console.log('Fetching new metrics for video:', video.video_id);
                newLoadingStates[video.video_id] = true;
                fetchPromises.push(
                    axios.get(
                        `${API.CORE}/api/v1/video-analytics/wistia/media/stats/${video.video_id}`,
                        {
                            headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json'
                            }
                        }
                    ).then(response => {
                        console.log('Received metrics for video:', video.video_id, response.data);
                        if (response.data) {
                            setCachedMetrics(video.video_id, response.data);
                            setMetrics(prev => {
                                const newMetrics = {
                                    ...prev,
                                    [video.video_id]: response.data
                                };
                                console.log('Updated metrics state:', newMetrics);
                                return newMetrics;
                            });
                        } else {
                            console.error('Empty response data for video:', video.video_id);
                        }
                        setViewsLoading(prev => ({
                            ...prev,
                            [video.video_id]: false
                        }));
                    }).catch(error => {
                        console.error(`Error fetching metrics for video ${video.video_id}:`, error.response || error);
                        // Set metrics to 0 on error
                        setMetrics(prev => ({
                            ...prev,
                            [video.video_id]: { play_count: 0 }
                        }));
                        setViewsLoading(prev => ({
                            ...prev,
                            [video.video_id]: false
                        }));
                    })
                );
            }
        });

        setViewsLoading(newLoadingStates);

        if (fetchPromises.length > 0) {
            try {
                await Promise.all(fetchPromises);
                console.log('All metrics fetched successfully');
            } catch (error) {
                console.error("Error updating video metrics:", error);
            }
        }
    }, [getCurrentPageItems]);

    // Setup metrics fetching when component mounts or page changes
    useEffect(() => {
        if (pageData.length > 0) {
            fetchVideoMetrics();
        }
    }, [pageData, fetchVideoMetrics]);

    // Remove polling-related code
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);

    // Render view count with loading state
    const renderViewCount = (video_id) => {
        if (viewsLoading[video_id]) {
            return <LoaderIcon className="animate-spin h-4 w-4" />;
        }
        const viewCount = metrics[video_id]?.play_count;
        return typeof viewCount === 'number' ? viewCount : 0;
    };

    // Handle page change
    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // Handle embed button click
    const handleEmbed = async (id) => {
        const item = filteredData.find((item) => item.id === id);
        const htmlContent = item?.embedCode;

        if (!htmlContent) {
            alert("Embed code not found.");
            return;
        }

        try {
            await navigator.clipboard.writeText(htmlContent);
            alert("Success! Embed content copied to clipboard.");
        } catch (err) {
            console.error("Copy failed:", err);
            alert("Failed to copy embed content.");
        }
    };

    // Get filter label for display
    const getFilterLabel = () => {
        switch (filterOption) {
            case "most_views":
                return "Most Views";
            case "no_views":
                return "No Views";
            default:
                return "All";
        }
    };

    // useEffect(() => {
    //     const videoEle = mockData[0];
    //     console.log(videoEle);

    //     const mediaId = videoEle["video_id"];

    //     const fetchStats = async () => {
    //         try {
    //             const response = await axios.get(
    //                 `https://api.wistia.com/v1/stats/medias/${mediaId}.json`,
    //                 {
    //                     headers: {
    //                         Accept: "application/json",
    //                         Authorization: `Bearer ${wistiaToken}`,
    //                     },
    //                 }
    //             );
    //             console.log("Wistia Media Stats:", response.data);
    //             videoEle.views = response.data.play_count;
    //             const dupData = [...data];
    //             dupData[0] = videoEle;
    //             setData(dupData);
    //         } catch (error) {
    //             console.error("Error fetching Wistia media stats:", error);
    //         }
    //     };

    //     fetchStats();
    // }, []);

    return (
        <VideoContainer>
            <VideoHeader>
                <VideoTitle>All Videos</VideoTitle>
                <SearchFilterContainer>
                    {isAdmin && (
                        <UniversitySelect size="small">
                            <InputLabel id="university-select-label">University</InputLabel>
                            <Select
                                labelId="university-select-label"
                                id="university-select"
                                value={selectedUniversity}
                                onChange={(e) => setSelectedUniversity(e.target.value)}
                                label="University"
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="all">All Universities</MenuItem>
                                <MenuItem value="boston">Boston</MenuItem>
                                <MenuItem value="usc">USC</MenuItem>
                                <MenuItem value="texas">Texas</MenuItem>
                            </Select>
                        </UniversitySelect>
                    )}
                    <TextField
                        variant="outlined"
                        placeholder="Search..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: "220px" }}
                    />
                    <FilterDropdownContainer ref={filterButtonRef}>
                        <FilterDropdownButton
                            variant="outlined"
                            startIcon={<FilterIcon />}
                            size="small"
                            onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                            sx={{
                                bgcolor: filterOption !== "all" ? "rgba(72, 128, 255, 0.1)" : "transparent",
                            }}
                        >
                            {getFilterLabel()}
                        </FilterDropdownButton>
                        <FilterDropdownContent isOpen={filterDropdownOpen}>
                            <FilterOption
                                className={filterOption === "all" ? "active" : ""}
                                onClick={() => handleFilterOptionClick("all")}
                            >
                                All
                            </FilterOption>
                            <FilterOption
                                className={filterOption === "most_views" ? "active" : ""}
                                onClick={() => handleFilterOptionClick("most_views")}
                            >
                                Most Views
                            </FilterOption>
                            <FilterOption
                                className={filterOption === "no_views" ? "active" : ""}
                                onClick={() => handleFilterOptionClick("no_views")}
                            >
                                No Views
                            </FilterOption>
                        </FilterDropdownContent>
                    </FilterDropdownContainer>
                </SearchFilterContainer>
            </VideoHeader>

            <div style={{ width: "100%", overflowX: "auto" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                backgroundColor: "#F0F4FF",
                                color: "#333",
                                fontFamily: "Nunito Sans, sans-serif",
                                fontSize: "14px",
                                fontWeight: 600,
                                textAlign: "left",
                            }}
                        >
                            <th style={{ padding: "16px", width: "5%" }}>
                                S.NO
                            </th>
                            <th style={{ padding: "16px", width: "10%" }}>
                                NAME
                            </th>
                            <th style={{ padding: "16px", width: "15%" }}>
                                PROGRAM
                            </th>
                            <th style={{ padding: "16px", width: "12%" }}>
                                COUNTRY
                            </th>
                            <th style={{ padding: "16px", width: "8%" }}>
                                VIEWS
                            </th>
                            <th style={{ padding: "16px", width: "15%" }}>
                                URL
                            </th>
                            <th
                                style={{
                                    padding: "16px",
                                    width: "10%",
                                    textAlign: "center",
                                }}
                            >
                                PREVIEW
                            </th>
                            <th
                                style={{
                                    padding: "16px",
                                    width: "13%",
                                    textAlign: "center",
                                }}
                            >
                                SHARE
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                    <LoaderIcon className="animate-spin" />
                                </td>
                            </tr>
                        ) : pageData.length > 0 ? (
                            pageData.map((row, index) => (
                                <tr
                                    key={row.video_id}
                                    style={{ borderBottom: "1px solid #eee" }}
                                >
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {index + 1}
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {row.name}
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {row.program}
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {row.country}
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {renderViewCount(row.video_id)}
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <a
                                            href={row.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: "#4880FF",
                                                textDecoration: "none",
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "100%",
                                            }}
                                            title={row.url}
                                        >
                                            {row.url.includes("drive.google.com")
                                                ? "https://drive.googl..."
                                                : row.url.includes("youtube.com")
                                                ? "https://www.youtu..."
                                                : row.url.length > 20
                                                ? row.url.substring(0, 20) + "..."
                                                : row.url}
                                        </a>
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            textAlign: "center",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const currentData =
                                                    filterOption === "most_views"
                                                        ? [...filteredData].sort(
                                                              (a, b) =>
                                                                  b.views - a.views
                                                          )
                                                        : filteredData;
                                                handleOpenPreview(
                                                    currentData.indexOf(row)
                                                );
                                            }}
                                            sx={{ color: "#4880FF" }}
                                            title="Preview video"
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                    <td
                                        style={{
                                            padding: "16px",
                                            textAlign: "center",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <EmbedButton
                                            variant="contained"
                                            onClick={() => handleEmbed(row.id)}
                                            size="small"
                                        >
                                            Embed
                                        </EmbedButton>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan="8"
                                    style={{
                                        padding: "32px",
                                        textAlign: "center",
                                        color: "#666",
                                    }}
                                >
                                    No videos found. Try adjusting your search
                                    or filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <PaginationContainer>
                <Pagination
                    count={Math.max(1, Math.ceil(filteredData.length / itemsPerPage))}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                />
            </PaginationContainer>

            {/* Video Preview Carousel */}
            <VideoPreviewCarousel
                open={previewOpen}
                onClose={handleClosePreview}
                videos={pageData}
                initialVideoIndex={selectedVideoIndex}
            />
        </VideoContainer>
    );
};

export default VideoEngagementTable;
